import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Breadcrumb } from './breadcrumbs.component';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly _breadcrumbs = new BehaviorSubject<Array<Breadcrumb>>([]);

  readonly breadcrumbs = this._breadcrumbs.asObservable();

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(
        // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        const { root } = this.router.routerState.snapshot;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);

        this._breadcrumbs.next(breadcrumbs);
      });
  }

  /**
   * `urlSegments` accumulates path segments as the tree is walked, so a level's own crumb is
   * built from its OWN cumulative path - not a parent's or child's. Passing a route's segments
   * to itself (rather than threading a level behind, as this used to) is what makes "click a
   * crumb to go back to that level" actually navigate to that level.
   */
  private addBreadcrumb(route: ActivatedRouteSnapshot | null, urlSegments: string[], breadcrumbs: Array<Breadcrumb>) {
    if (!route) {
      return;
    }
    const ownSegments = (route.url ?? []).map((segment) => segment.path).filter((path) => !!path);
    const cumulativeSegments = [...urlSegments, ...ownSegments];
    // Add an element for the current route part
    if (route.data.breadcrumb && route.component) {
      breadcrumbs.push(this.getBreadcrumb(route, cumulativeSegments));
    }
    // Add another element for the next route part
    this.addBreadcrumb(route.firstChild, cumulativeSegments, breadcrumbs);
  }

  private getBreadcrumb(route: ActivatedRouteSnapshot, urlSegments: string[]): Breadcrumb {
    const breadcrumb = route.data.breadcrumb;

    switch (typeof breadcrumb) {
      case 'function':
        return breadcrumb(route.data);
      case 'string':
        return { name: breadcrumb, navigate: urlSegments.length > 0 ? ['/', ...urlSegments] : undefined };
      default:
        // Shallow-cloned: `breadcrumb` is the literal object stored in `route.data.breadcrumb`,
        // and `Object.freeze(inherited.data)` only freezes the top level, so returning it as-is
        // would let a consumer's in-place mutation (e.g. `crumb.queryParams = ...`) leak
        // permanently into the route config for every future navigation.
        return { ...breadcrumb };
    }
  }
}
